import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { applyTheme, isValidThemeName } from '../styles/themeUtils';
import { DEFAULT_THEME, type ThemeName } from '../styles/themes';
import type { ThemeCache } from './themeCache';

/**
 * Makes the current tenant readable anywhere in the tree, and applies its
 * white-label palette to `<html>`.
 *
 * ## This is a context boundary, not an identity mechanism
 *
 * Every app already knows how to answer "who is the current tenant?" and they do
 * it differently — a Capacitor plugin, a `?tenant-handle=` URL param, a
 * `{handle}.buttersoftware.com` subdomain. This provider resolves NOTHING. It
 * takes an already-resolved handle and branding value as props and does two jobs:
 *
 * 1. Applies the palette (`--theme-*` custom properties on `<html>`).
 * 2. Publishes `{ handle, colorTheme }` so shared components — `TenantLogo`,
 *    `TenantBrand`, `TenantIcon` — stop needing the handle threaded through props.
 *
 * Both branding concerns are downstream of the one lookup: the logo needs only the
 * handle, the palette needs one value from the same `tenants` row.
 *
 * ## How a palette gets applied, in order of arrival
 *
 * 1. **Cached (instant):** on mount, read the last-known palette from `cache`. A
 *    synchronous adapter applies it during the first render — no flash. An async
 *    one (Capacitor `Preferences`) applies it a microtask later.
 * 2. **Prop (authoritative):** whenever `colorTheme` resolves, apply and persist.
 * 3. **Imperative:** `useTenant().setColorTheme(name)` for flows that switch
 *    tenants mid-session and can't wait for a prop round-trip.
 *
 * With nothing available the `:root` defaults in `tokens.css` apply, so children
 * always render against a valid palette and never need to wait.
 *
 * @example web portal — handle from the URL, palette from the club payload
 *   const [handle] = useTenantHandle();
 *   const cache = useMemo(() => (handle ? createThemeCache(handle) : undefined), [handle]);
 *   <TenantProvider handle={handle} colorTheme={club?.tenant?.colorTheme} cache={cache}>
 *
 * @example mobile — handle from the plugin, Capacitor-backed cache
 *   <TenantProvider handle={handle} colorTheme={theme} cache={preferencesThemeCache}>
 */
export interface TenantProviderProps {
  children: ReactNode;
  /**
   * The resolved tenant handle — the key the logo registry and subdomains use.
   * `null`/`undefined` while the app hasn't resolved one (generic app, first
   * launch, no URL param), in which case logos fall back to Butter.
   */
  handle?: string | null;
  /**
   * The tenant's palette name, unvalidated — raw API strings are fine, this
   * narrows them itself.
   *
   * The two empty values mean different things:
   * - `undefined` — not resolved yet. Leaves the cached/default palette alone.
   * - `null` — resolved, and this tenant has no palette. Applies {@link DEFAULT_THEME}.
   *
   * An **unrecognized** name keeps whatever is currently applied and warns. It is
   * deliberately NOT coerced to the default: an older shipped bundle (a mobile app
   * behind on OTA updates) meeting a newly-added palette must keep the tenant's
   * last-known brand rather than overwrite a correct cached value with purple.
   */
  colorTheme?: string | null;
  /**
   * Persistence for the palette. **Omit for no persistence** — there is no
   * default, because an unscoped default cache is exactly how cross-tenant bleed
   * happens. Build one with `createThemeCache(handle)`.
   *
   * **Read once, on the first render.** A cache that only appears on a later
   * render is never read (it is still written to). That is not a limitation worth
   * routing around: the cache key is derived from the handle, so a cache arriving
   * late means the handle arrived late — and every app resolves its palette from
   * the same source as its handle, so by then the authoritative value is already
   * in hand and there is nothing left to paint early. Applying a late cached value
   * would only risk one frame of a stale palette before the fresh one lands.
   *
   * Mid-session tenant switches are covered by `setColorTheme`, not by re-reading.
   */
  cache?: ThemeCache;
}

export interface TenantContextValue {
  /** The resolved tenant handle, or `null` when the app has none. */
  handle: string | null;
  /** The palette currently applied to `<html>`, or `null` before anything resolves. */
  colorTheme: ThemeName | null;
  /**
   * Apply and persist a palette immediately. Mirrors the `colorTheme` prop's
   * empty-value rules exactly: `undefined` is a no-op (nothing resolved),
   * `null` applies the default, and an unrecognized name leaves the current
   * palette standing.
   */
  setColorTheme: (themeName: string | null | undefined) => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

// `useLayoutEffect` runs before the browser paints, which is what makes the
// cached palette flash-free. It warns when there is no DOM, and @butter/ui is
// consumed by apps/marketing-site, which is `ssr: false` but `prerender: true` —
// so its routes are still rendered in Node at build time. Fall back to
// `useEffect` there.
const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

export function TenantProvider({ children, handle, colorTheme, cache }: TenantProviderProps) {
  // Read the cache EXACTLY ONCE per mount. A lazy ref rather than a call in the
  // useState initializer: initializers re-run under StrictMode's double-invoke,
  // and an async adapter would start (and discard) a second read.
  //
  // This one read DOES happen during render, unlike the `cacheRef` write below
  // that is deliberately deferred to a layout effect. Lazy ref initialization is a
  // sanctioned React pattern, and the two cases aren't equivalent: `get()` is
  // idempotent and side-effect-free, so a render that concurrent React discards
  // costs at most a duplicate read. Deferring it instead would give up the
  // synchronous first-paint hit that is the entire reason phase 1 exists.
  const cachedRef = useRef<string | null | Promise<string | null> | undefined>(undefined);
  if (cachedRef.current === undefined) {
    cachedRef.current = cache ? cache.get() : null;
  }

  // `useRef`, not state: a slow async cache read must not clobber a palette that a
  // prop or setColorTheme() already applied, and that check has to see the latest
  // value without waiting for a re-render.
  const resolvedRef = useRef(false);

  // Keep the cache in a ref so `applyAndPersist` stays identity-stable. Without
  // this, an inline `createThemeCache(handle)` in a consumer's render would give
  // the callback a new identity every render, and the prop effect below would fire
  // applyTheme + cache.set on EVERY render.
  //
  // Assigned in a layout effect rather than during render: mutating a ref while
  // rendering is unsafe under concurrent React (an interrupted render still
  // mutates). Safe to defer because `applyAndPersist` only ever runs from effects
  // and event handlers, never during render — so the ref is always current by the
  // time anything reads it.
  const cacheRef = useRef(cache);
  useIsomorphicLayoutEffect(() => {
    cacheRef.current = cache;
  });

  // Pure initializer — no DOM writes during render. The cached value is applied in
  // the layout effect below, which still runs before the first paint.
  const [activeTheme, setActiveTheme] = useState<ThemeName | null>(() => {
    const cached = cachedRef.current;
    if (cached instanceof Promise || !cached || !isValidThemeName(cached)) return null;
    return cached;
  });

  const applyAndPersist = useCallback((raw: string | null | undefined) => {
    // Not resolved — exactly what `undefined` means on the `colorTheme` prop, and
    // it has to mean the same thing here. Coercing it to the default would paint
    // Butter purple over a correct cached brand AND persist it, which is the one
    // outcome this module is built to prevent.
    if (raw === undefined) return;

    // Resolved, but we don't recognize it — keep what's applied. See `colorTheme`.
    if (raw !== null && !isValidThemeName(raw)) {
      console.warn(
        `[TenantProvider] Unknown theme "${raw}" — keeping the current theme. ` +
          'This build has no palette registered under that name.',
      );
      return;
    }

    const name: ThemeName = raw ?? DEFAULT_THEME;
    resolvedRef.current = true;
    applyTheme(name);
    setActiveTheme(name);
    // Adapters never throw, but an async `set` still rejects on its own timeline.
    void Promise.resolve(cacheRef.current?.set(name)).catch(() => {});
  }, []);

  // Phase 1a — synchronous cache hit, applied before the first paint.
  useIsomorphicLayoutEffect(() => {
    if (activeTheme) applyTheme(activeTheme);
    // Mount only: later changes flow through applyAndPersist, which applies as it goes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 1b — asynchronous cache hit (Capacitor Preferences and friends).
  // Skipped entirely when the adapter answered synchronously above.
  useEffect(() => {
    const cached = cachedRef.current;
    if (!(cached instanceof Promise)) return;

    let cancelled = false;
    cached
      .then((value) => {
        // A prop or setColorTheme() beat the cache to it — that value is fresher.
        if (cancelled || resolvedRef.current) return;
        if (value && isValidThemeName(value)) {
          applyTheme(value);
          setActiveTheme(value);
        }
      })
      .catch(() => {
        // A cache miss isn't worth surfacing — the `:root` defaults hold.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Phase 2 — the authoritative value. `undefined` means the app hasn't resolved a
  // tenant yet, so leave whatever the cache painted in place. `applyAndPersist`
  // enforces this too (it is public API via `setColorTheme`); this check just
  // avoids the call.
  useEffect(() => {
    if (colorTheme === undefined) return;
    applyAndPersist(colorTheme);
  }, [colorTheme, applyAndPersist]);

  // `applyAndPersist` is identity-stable, so this only changes when the tenant or
  // the applied palette actually changes — consumers don't re-render on every
  // parent render.
  const value = useMemo<TenantContextValue>(
    () => ({
      handle: handle ?? null,
      colorTheme: activeTheme,
      setColorTheme: applyAndPersist,
    }),
    [handle, activeTheme, applyAndPersist],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

/**
 * Read the current tenant, or change its palette.
 *
 * Returns `null` outside a provider rather than throwing — shared components
 * (`TenantLogo` and friends) read this to default their `tenantHandle` prop, and
 * they must keep working when rendered standalone, as the Storybook tuning stories
 * do.
 *
 * Use `setColorTheme` for flows that change tenant mid-session and need the new
 * brand on the very next screen: it applies and persists in one call, so no caller
 * has to pair `applyTheme()` with a cache write by hand.
 */
export function useTenant(): TenantContextValue | null {
  return useContext(TenantContext);
}
